<script setup>
// Phase 4.5: the notice is the kit's Alert (OverviewBanner); same rule for when it shows and how it is dismissed.
import { computed, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import OverviewBanner from './OverviewBanner.vue';
import { LocalStorage } from 'shared/helpers/localStorage';

const props = defineProps({
  knowledge: {
    type: Object,
    default: () => ({ approved: 0, pending: 0, documents: 0, coverage: 0 }),
  },
});

const route = useRoute();
const router = useRouter();

// Dismissal is remembered per assistant for 24 hours (setFlag's default expiry).
const DISMISS_STORE = 'polaris_overview_coverage_banner';

const accountId = computed(() => route.params.accountId);
const assistantId = computed(() => route.params.assistantId);

// Re-read the stored flag whenever the assistant changes, otherwise the banner
// would keep the first assistant's dismissed state after switching.
const dismissed = ref(false);

watch(
  [accountId, assistantId],
  ([account, assistant]) => {
    dismissed.value = LocalStorage.getFlag(DISMISS_STORE, account, assistant);
  },
  { immediate: true }
);

// Thin coverage paired with a large review backlog: approving the pending FAQs
// is the quickest lever to lift auto-resolution, so nudge the team to act.
const COVERAGE_THRESHOLD = 85;
const PENDING_THRESHOLD = 100;

const showBanner = computed(
  () =>
    !dismissed.value &&
    (props.knowledge?.coverage ?? 0) < COVERAGE_THRESHOLD &&
    (props.knowledge?.pending ?? 0) > PENDING_THRESHOLD
);

const dismiss = () => {
  LocalStorage.setFlag(DISMISS_STORE, accountId.value, assistantId.value);
  dismissed.value = true;
};

const goToPending = () => {
  router.push({
    name: 'polaris_assistants_responses_pending',
    params: {
      accountId: route.params.accountId,
      assistantId: route.params.assistantId,
    },
  });
};
</script>

<!-- eslint-disable-next-line vue/no-root-v-if -->
<template>
  <OverviewBanner
    v-if="showBanner"
    :text="
      $t('POLARIS.OVERVIEW.COVERAGE_BANNER.TEXT', {
        count: knowledge.pending,
        coverage: knowledge.coverage,
      })
    "
    :action-label="$t('POLARIS.OVERVIEW.COVERAGE_BANNER.ACTION')"
    :dismiss-label="$t('POLARIS.OVERVIEW.COVERAGE_BANNER.DISMISS')"
    @action="goToPending"
    @dismiss="dismiss"
  />
</template>
